import { Feature } from "geojson";
import { createContext, useContext, useState } from "react";
import { PropriedadesFeicao } from "store/modules/consultaprevia/types";

export type OrigemCamada = "zona" | "area";

/**
 * Identidade da camada na lista de visibilidade. Usa o id da feição — a sigla
 * serve de reserva para o caso de uma camada sem id vir do backend.
 */
export const chaveDaFeicao = (
  feature: Feature,
  origem: OrigemCamada
): string => {
  const props = (feature.properties ?? {}) as Partial<PropriedadesFeicao>;

  return `${origem}:${props.id ?? props.sigla ?? ""}`;
};

interface Context {
  /** Camadas desligadas. Exposto para servir de `key` na remontagem do GeoJSON. */
  ocultas: string[];
  estaVisivel: (chave: string) => boolean;
  alternarCamada: (chave: string) => void;
  /** Liga ou desliga um grupo inteiro de uma vez. */
  definirVisibilidade: (chaves: string[], visivel: boolean) => void;
  /** Sigla realçada no mapa (vem da busca, da lista ou da ficha aberta). */
  siglaSelecionada: string | null;
  selecionarSigla: (sigla: string | null) => void;
  /**
   * A ficha de uma zona está ocupando a lateral. A consulta no ponto se recolhe
   * enquanto isso, em vez de ser descartada — é o que dá sentido ao "Voltar".
   */
  fichaAberta: boolean;
  definirFichaAberta: (aberta: boolean) => void;
}

const CamadasContext = createContext<Context>({
  ocultas: [],
  estaVisivel: () => true,
  alternarCamada: () => null,
  definirVisibilidade: () => null,
  siglaSelecionada: null,
  selecionarSigla: () => null,
  fichaAberta: false,
  definirFichaAberta: () => null
});

/**
 * Estado de exibição do mapa da consulta prévia. Fica em contexto, e não em
 * redux, porque é preferência de visualização da sessão: não sobrevive à saída
 * da tela nem interessa a mais ninguém.
 *
 * A lista guarda o que está **oculto**, não o que está visível: camada nova
 * que chegue do backend aparece por padrão, em vez de sumir por não constar
 * de uma lista de permitidos.
 */
const Provider: React.FC = ({ children }) => {
  const [ocultas, setOcultas] = useState<string[]>([]);
  const [siglaSelecionada, setSiglaSelecionada] = useState<string | null>(null);
  const [fichaAberta, setFichaAberta] = useState(false);

  const estaVisivel = (chave: string) => !ocultas.includes(chave);

  const alternarCamada = (chave: string) =>
    setOcultas((atuais) =>
      atuais.includes(chave)
        ? atuais.filter((item) => item !== chave)
        : [...atuais, chave]
    );

  const definirVisibilidade = (chaves: string[], visivel: boolean) =>
    setOcultas((atuais) =>
      visivel
        ? atuais.filter((item) => !chaves.includes(item))
        : Array.from(new Set([...atuais, ...chaves]))
    );

  return (
    <CamadasContext.Provider
      value={{
        ocultas,
        estaVisivel,
        alternarCamada,
        definirVisibilidade,
        siglaSelecionada,
        selecionarSigla: setSiglaSelecionada,
        fichaAberta,
        definirFichaAberta: setFichaAberta
      }}
    >
      {children}
    </CamadasContext.Provider>
  );
};

export const useCamadas = (): Context => useContext(CamadasContext);

export default Provider;
